import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { Fingerprint, ShieldCheck, Trash2, Plus, Copy } from 'lucide-react';
import { Panel, Badge, Empty } from '../../components/Primitives';
import { api, errMsg, fmtDate, shortHash } from '../../lib/api';
import { registerPasskey, passkeySupported } from '../../lib/passkeys';
import { useAuth } from '../../contexts/AuthContext';

const inp = 'w-full h-11 px-3 rounded-lg bg-vj-charcoal border border-border text-sm text-foreground focus:border-vj-neon/60 focus:outline-none';

export default function Security() {
  const { refresh } = useAuth();
  const [sec, setSec] = useState(null);
  const [resetEmail, setResetEmail] = useState('');

  // TOTP state
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState([]);
  const [disableCode, setDisableCode] = useState('');

  const load = () => api.get('/auth/security').then(({ data }) => setSec(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendReset = async () => {
    if (!resetEmail) return toast.error('Enter your account email');
    try { await api.post('/auth/forgot-password', { email: resetEmail }); toast.success('Password reset link generated (dev mode — see server logs)'); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const addPasskey = async () => {
    try {
      const name = window.prompt('Name this passkey (e.g. MacBook, iPhone):', 'My device') || 'Passkey';
      await registerPasskey(name);
      toast.success('Passkey added'); load(); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || 'Could not add passkey'); }
  };

  const removePasskey = async (id) => {
    try { await api.delete(`/auth/passkeys/${encodeURIComponent(id)}`); toast.success('Passkey removed'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const setupTotp = async () => {
    try {
      const { data } = await api.post('/auth/totp/setup');
      setSecret(data.secret);
      setQr(await QRCode.toDataURL(data.provisioning_uri, { margin: 1, color: { dark: '#39FF14', light: '#101512' } }));
    } catch (err) { toast.error(errMsg(err)); }
  };

  const enableTotp = async () => {
    try {
      const { data } = await api.post('/auth/totp/enable', { code: code.trim() });
      setRecovery(data.recovery_codes); setQr(''); setSecret(''); setCode('');
      toast.success('Two-factor authentication enabled'); load(); refresh();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const disableTotp = async () => {
    try {
      await api.post('/auth/totp/disable', { code: disableCode.trim() });
      setDisableCode(''); toast.success('2FA disabled'); load(); refresh();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const totpEnabled = sec?.totp_enabled;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="security-page">
      {/* Passkeys */}
      <Panel title="Passkeys (WebAuthn)" action={passkeySupported() && (
        <button onClick={addPasskey} className="h-9 px-3 rounded-lg bg-vj-neon/15 text-vj-neon text-xs inline-flex items-center gap-1.5 border border-vj-neon/30" data-testid="add-passkey-btn"><Plus size={13} /> Add passkey</button>
      )}>
        <p className="text-sm text-vj-grey mb-4">Sign in with your device biometrics or a hardware security key — no password needed.</p>
        {sec?.passkeys?.length ? (
          <div className="space-y-2">
            {sec.passkeys.map((p) => (
              <div key={p.credential_id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/5 grid place-items-center"><Fingerprint size={16} className="text-vj-neon" /></div>
                  <div><div className="text-sm">{p.name}</div><div className="text-xs text-vj-grey">{shortHash(p.credential_id)} · added {fmtDate(p.created_at)}</div></div>
                </div>
                <button onClick={() => removePasskey(p.credential_id)} className="text-vj-grey hover:text-vj-red" data-testid={`remove-passkey-${p.credential_id}`}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        ) : <Empty>{passkeySupported() ? 'No passkeys yet. Add one for fast, phishing-resistant sign-in.' : 'This browser does not support passkeys.'}</Empty>}
      </Panel>

      {/* TOTP */}
      <Panel title="Authenticator App (2FA)" action={<Badge tone={totpEnabled ? 'green' : 'grey'}>{totpEnabled ? 'Enabled' : 'Disabled'}</Badge>}>
        {!totpEnabled && !recovery.length && (
          <>
            <p className="text-sm text-vj-grey mb-4">Protect your account with a time-based one-time code from Google Authenticator, Authy, or 1Password.</p>
            {!qr ? (
              <button onClick={setupTotp} className="h-11 px-5 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm vj-glow-green hover:-translate-y-0.5 transition-all" data-testid="totp-setup-btn">Set up authenticator</button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <img src={qr} alt="2FA QR" className="rounded-lg border border-vj-neon/30 w-44 h-44" data-testid="totp-qr" />
                <div className="flex-1 space-y-3">
                  <p className="text-xs text-vj-grey">Scan the QR with your authenticator app, or enter this secret manually:</p>
                  <div className="font-mono text-xs break-all p-2 rounded bg-vj-charcoal border border-border flex items-center justify-between gap-2">
                    <span>{secret}</span>
                    <button onClick={() => { navigator.clipboard.writeText(secret); toast.success('Secret copied'); }} className="text-vj-grey hover:text-vj-neon"><Copy size={14} /></button>
                  </div>
                  <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter 6-digit code" className={`${inp} font-mono tracking-widest`} data-testid="totp-code" />
                  <button onClick={enableTotp} className="h-11 px-5 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm vj-glow-green" data-testid="totp-enable-btn">Verify & enable</button>
                </div>
              </div>
            )}
          </>
        )}
        {recovery.length > 0 && (
          <div className="rounded-lg border border-vj-gold/30 bg-vj-gold/5 p-4" data-testid="recovery-codes">
            <div className="text-sm font-medium text-vj-gold mb-2">Save your recovery codes (shown once)</div>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {recovery.map((c) => <div key={c} className="px-2 py-1 rounded bg-vj-charcoal">{c}</div>)}
            </div>
            <button onClick={() => setRecovery([])} className="mt-3 text-xs text-vj-neon">I've saved them</button>
          </div>
        )}
        {totpEnabled && (
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-xs text-vj-grey">Enter a current code to disable 2FA</label>
              <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456" className={`${inp} font-mono tracking-widest mt-1`} data-testid="totp-disable-code" />
            </div>
            <button onClick={disableTotp} className="h-11 px-5 rounded-lg border border-vj-red/40 text-vj-red text-sm" data-testid="totp-disable-btn">Disable</button>
          </div>
        )}
      </Panel>

      {/* Password */}
      <Panel title="Change Password">
        <div className="flex gap-2">
          <input value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="Account email" className={inp} data-testid="security-email" />
          <button onClick={sendReset} className="h-11 px-5 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm whitespace-nowrap vj-glow-green" data-testid="security-reset-btn">Send reset link</button>
        </div>
      </Panel>

      {/* Sessions */}
      <Panel title="Recent Sessions">
        {sec?.sessions?.length ? (
          <div className="space-y-2">
            {sec.sessions.map((s, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm">
                <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-vj-neon" /> {s.ip}</span>
                <span className="text-xs text-vj-grey truncate max-w-[280px]">{s.ua}</span>
                <span className="text-xs text-vj-grey">{fmtDate(s.at)}</span>
              </div>
            ))}
          </div>
        ) : <Empty>No session history.</Empty>}
      </Panel>
    </div>
  );
}
